import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import type { Environment } from "@/types/common";
import * as Sentry from "@sentry/react";
import { getEnv } from "@/helpers/env.helpers";

const enabledEnvs: Environment[] = ["prod"];
const URL_IGNORE_LIST = [
  "contentsquare.net",
  "contentsquare.com",
  "heap-api.com",
  "heapanalytics.com",
];

const environment = getEnv();
const SENTRY_DSN = String(import.meta.env.VITE_SENTRY_DSN ?? "");
const sentryAppKey = String(import.meta.env.VITE_SENTRY_DSN ?? "");
const enabled = enabledEnvs.includes(environment);

// Note: we init sentry in all environments so we can catch any regressions with the way it spies on fetch/xhr.
// In local you can provide the SENTRY_DSN in .env.local
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    enabled,
    environment,

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.captureConsoleIntegration({ levels: ["error", "log"] }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [sentryAppKey],
        // Defines how to handle errors that contain third party stack frames.
        // see https://docs.sentry.io/platforms/javascript/configuration/filtering/
        behaviour: "drop-error-if-contains-third-party-frames",
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [],
    sendDefaultPii: false,

    beforeBreadcrumb(breadcrumb, _hint) {
      const { category, data } = breadcrumb;
      if (
        category === "http" &&
        URL_IGNORE_LIST.some((p) => String(data?.url).includes(p))
      ) {
        return null;
      }
      return breadcrumb;
    },
  });
} else {
  // eslint-disable-next-line no-console
  console.warn("Observability not initialised (SENTRY_DSN not defined)");
}
